/**
 * Family / household: every family this member belongs to, with the other people in it.
 * Self mode is read-only (GET /me/family). Admin mode (GET /{memberId}/families) links each person
 * to their own profile and, with Member.Family.Write, can create a household, add people, change
 * relationships, rename and remove (api/families).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconUsersGroup, IconPlus, IconPencil, IconTrash, IconUserMinus, IconArrowsExchange, IconAlertCircle, IconHomePlus,
} from '@tabler/icons-react';
import {
  Card, Stack, Group, Button, Text, Badge, Avatar, Alert, Modal, Skeleton, Menu, ActionIcon, TextInput, Select, SegmentedControl, EmptyState,
} from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import { useFamilyService, type Family, type Member, type MemberFamilyView, type FamilyMemberModelView } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { useRelationships } from '../useMemberLookups';
import { MemberSearchSelect } from '../MemberSearchSelect';
import { errorMessage, initialsOf, memberFullName, ageFromDob } from '../memberFormat';

type Dialog =
  | { kind: 'join' }
  | { kind: 'add-person'; family: MemberFamilyView }
  | { kind: 'relationship'; family: MemberFamilyView; person: FamilyMemberModelView }
  | { kind: 'rename'; family: MemberFamilyView }
  | null;

export function FamilyTab({ api, member, perms }: MemberTabProps) {
  const queryClient = useQueryClient();
  const familyService = useFamilyService();
  const isAdmin = api.mode === 'admin';
  const families = useQuery({ queryKey: api.keys.families, queryFn: () => api.getFamilies(), enabled: perms.familyRead });
  const { options: relationshipOptions } = useRelationships(isAdmin && perms.familyWrite);

  const [dialog, setDialog] = useState<Dialog>(null);
  const [joinMode, setJoinMode] = useState<'new' | 'existing'>('new');
  const [familyName, setFamilyName] = useState('');
  const [existingFamilyId, setExistingFamilyId] = useState<string | null>(null);
  const [relationshipId, setRelationshipId] = useState<string | null>(null);
  const [person, setPerson] = useState<Member | null>(null);

  const allFamilies = useQuery({
    queryKey: ['members', 'families', 'all'],
    queryFn: () => familyService.getAll(),
    enabled: dialog?.kind === 'join' && joinMode === 'existing',
    staleTime: 60_000,
  });
  const familyOptions = useMemo(() => {
    const mine = new Set((families.data ?? []).map((f) => f.familyID));
    return (allFamilies.data ?? [])
      .filter((f: Family) => !mine.has(f.familyID))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((f) => ({ value: String(f.familyID), label: f.name }));
  }, [allFamilies.data, families.data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: api.keys.families });
  const close = () => { setDialog(null); setFamilyName(''); setExistingFamilyId(null); setRelationshipId(null); setPerson(null); };

  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { refresh(); close(); },
    onError: (e) => toast.error('Could not update the household', errorMessage(e)),
  });

  const join = () => act.mutate(async () => {
    let familyId = existingFamilyId ? Number(existingFamilyId) : 0;
    if (joinMode === 'new') {
      const created = await familyService.create({ familyID: 0, name: familyName.trim(), createdDateTime: 0, modifiedDateTime: 0 });
      familyId = created.familyID;
    }
    await familyService.addMember(familyId, { memberId: member.memberID, relationshipId: Number(relationshipId) });
    toast.success('Household updated', joinMode === 'new' ? `${familyName.trim()} created.` : 'Added to the household.');
  });

  const addPerson = (family: MemberFamilyView) => act.mutate(async () => {
    if (!person) return;
    await familyService.addMember(family.familyID, { memberId: person.memberID, relationshipId: Number(relationshipId) });
    toast.success('Household updated', `${memberFullName(person)} added to ${family.familyName ?? 'the household'}.`);
  });

  const changeRelationship = (family: MemberFamilyView, p: FamilyMemberModelView) => act.mutate(async () => {
    await familyService.updateMemberRelationship(family.familyID, p.memberID, Number(relationshipId));
    toast.success('Relationship updated');
  });

  const rename = (family: MemberFamilyView) => act.mutate(async () => {
    await familyService.rename(family.familyID, familyName.trim());
    toast.success('Household renamed');
  });

  const removePerson = (family: MemberFamilyView, p: FamilyMemberModelView) => modal.confirm({
    title: `Remove ${p.fullName || memberFullName(p) || 'this person'} from ${family.familyName ?? 'the household'}?`,
    body: 'Only the household link is removed. The member record stays.',
    confirmLabel: 'Remove',
    confirmColor: 'red',
    onConfirm: () => act.mutate(async () => {
      await familyService.removeMember(family.familyID, p.memberID);
      toast.success('Removed from the household');
    }),
  });

  const deleteFamily = (family: MemberFamilyView) => modal.confirm({
    title: `Delete ${family.familyName ?? 'this household'}?`,
    body: 'The household and all its links are deleted. Every member record stays.',
    confirmLabel: 'Delete household',
    confirmColor: 'red',
    onConfirm: () => act.mutate(async () => {
      await familyService.delete(family.familyID);
      toast.success('Household deleted');
    }),
  });

  if (!perms.familyRead) {
    return <Alert color="yellow" icon={<IconAlertCircle size={16} />}>You do not have access to household details.</Alert>;
  }

  const list = families.data ?? [];
  const canWrite = isAdmin && perms.familyWrite;

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Family and household</Text>
            <Text size="sm" c="dimmed">
              {isAdmin ? 'Households this person belongs to. Click a name to open that profile.' : 'The households you belong to. Ask the church office to make changes.'}
            </Text>
          </div>
          {canWrite && (
            <Button size="sm" leftSection={<IconHomePlus size={16} />}
              onClick={() => { setJoinMode('new'); setFamilyName(`${(member.lastName ?? '').trim() || memberFullName(member)} family`); setDialog({ kind: 'join' }); }}>
              Add to a household
            </Button>
          )}
        </Group>
      </Card>

      {families.isLoading && <Skeleton height={140} radius="md" />}
      {families.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load households">{errorMessage(families.error)}</Alert>}
      {!families.isLoading && !families.isError && list.length === 0 && (
        <EmptyState size="sm" icon={<IconUsersGroup size={32} />} title="Not part of a household yet"
          description={canWrite ? 'Create a household or add this person to an existing one.' : 'No household has been recorded.'} />
      )}

      {list.map((family) => (
        <Card key={family.familyID} withBorder padding="lg" radius="md">
          <Group justify="space-between" mb="sm" align="flex-start">
            <div>
              <Group gap="xs">
                <IconUsersGroup size={18} />
                <Text fw={600}>{family.familyName || `Household ${family.familyID}`}</Text>
              </Group>
              {family.relationshipName && (
                <Text size="sm" c="dimmed">{isAdmin ? memberFullName(member) : 'You'}: {family.relationshipName}</Text>
              )}
            </div>
            {canWrite && (
              <Group gap="xs">
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => { setRelationshipId(null); setPerson(null); setDialog({ kind: 'add-person', family }); }}>
                  Add a person
                </Button>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target><ActionIcon variant="subtle" aria-label="Household actions"><IconPencil size={16} /></ActionIcon></Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => { setFamilyName(family.familyName ?? ''); setDialog({ kind: 'rename', family }); }}>Rename household</Menu.Item>
                    {perms.familyDelete && (
                      <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => deleteFamily(family)}>Delete household</Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </Group>

          <Stack gap="xs">
            {[...family.members].sort((a, b) => (ageFromDob(b.dateOfBirth) ?? 0) - (ageFromDob(a.dateOfBirth) ?? 0)).map((p) => {
              const name = p.fullName || memberFullName(p) || `Member ${p.memberID}`;
              const isThis = p.memberID === member.memberID;
              const age = ageFromDob(p.dateOfBirth);
              return (
                <Group key={p.memberID} justify="space-between" wrap="nowrap" p="xs"
                  style={{ borderRadius: 8, background: isThis ? 'var(--mantine-color-default-hover)' : undefined }}>
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Avatar src={p.avatar || undefined} radius="xl" size="md">{initialsOf(name)}</Avatar>
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      {isAdmin && !isThis ? (
                        <Link to={`/admin/members/${p.memberID}`} style={{ fontWeight: 600, fontSize: 14, color: 'var(--mantine-color-anchor)', textDecoration: 'none' }}>{name}</Link>
                      ) : (
                        <Text size="sm" fw={600}>{name}{isThis ? (isAdmin ? ' (this profile)' : ' (you)') : ''}</Text>
                      )}
                      <Text size="xs" c="dimmed">{[age != null ? `${age} years` : null, p.memberCode, p.cellNumber].filter(Boolean).join(' · ') || 'No details'}</Text>
                    </Stack>
                  </Group>
                  <Group gap="xs" wrap="nowrap">
                    {p.relationName && <Badge variant="light" style={p.relationColor ? { backgroundColor: `${p.relationColor}22`, color: p.relationColor } : undefined}>{p.relationName}</Badge>}
                    {p.isActive === false && <Badge variant="light" color="gray">Inactive</Badge>}
                    {canWrite && (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target><ActionIcon variant="subtle" aria-label="Person actions"><IconArrowsExchange size={16} /></ActionIcon></Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconArrowsExchange size={14} />}
                            onClick={() => { setRelationshipId(p.relationshipID != null ? String(p.relationshipID) : null); setDialog({ kind: 'relationship', family, person: p }); }}>
                            Change relationship
                          </Menu.Item>
                          {perms.familyDelete && (
                            <Menu.Item color="red" leftSection={<IconUserMinus size={14} />} onClick={() => removePerson(family, p)}>Remove from household</Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Group>
              );
            })}
          </Stack>
        </Card>
      ))}

      {/* Add this member to a household */}
      <Modal opened={dialog?.kind === 'join'} onClose={close} title="Add to a household" centered size="md">
        <Stack gap="md">
          <SegmentedControl fullWidth value={joinMode} onChange={(v) => setJoinMode(v as 'new' | 'existing')}
            data={[{ value: 'new', label: 'New household' }, { value: 'existing', label: 'Existing household' }]} />
          {joinMode === 'new' ? (
            <TextInput label="Household name" required value={familyName} onChange={(e) => setFamilyName(e.currentTarget.value)} />
          ) : (
            <Select label="Household" placeholder={allFamilies.isLoading ? 'Loading households' : 'Search households'} searchable required
              data={familyOptions} value={existingFamilyId} onChange={setExistingFamilyId} nothingFoundMessage="No households found" />
          )}
          <Select label={`${memberFullName(member) || 'This person'} is the`} placeholder="Choose a relationship" required data={relationshipOptions}
            value={relationshipId} onChange={setRelationshipId} />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>Cancel</Button>
            <Button loading={act.isPending} disabled={!relationshipId || (joinMode === 'new' ? !familyName.trim() : !existingFamilyId)} onClick={join}>Add</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add another person to a household */}
      <Modal opened={dialog?.kind === 'add-person'} onClose={close} centered size="md"
        title={dialog?.kind === 'add-person' ? `Add a person to ${dialog.family.familyName ?? 'the household'}` : ''}>
        {dialog?.kind === 'add-person' && (
          <Stack gap="md">
            <MemberSearchSelect label="Person" required value={person} onChange={setPerson}
              excludeIds={dialog.family.members.map((m) => m.memberID)} />
            <Select label="Relationship" placeholder="Choose a relationship" required data={relationshipOptions} value={relationshipId} onChange={setRelationshipId} />
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>Cancel</Button>
              <Button loading={act.isPending} disabled={!person || !relationshipId} onClick={() => addPerson(dialog.family)}>Add</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Change a relationship */}
      <Modal opened={dialog?.kind === 'relationship'} onClose={close} centered size="sm" title="Change relationship">
        {dialog?.kind === 'relationship' && (
          <Stack gap="md">
            <Text size="sm">{dialog.person.fullName || memberFullName(dialog.person)} in {dialog.family.familyName ?? 'the household'}</Text>
            <Select label="Relationship" required data={relationshipOptions} value={relationshipId} onChange={setRelationshipId} />
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>Cancel</Button>
              <Button loading={act.isPending} disabled={!relationshipId} onClick={() => changeRelationship(dialog.family, dialog.person)}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Rename */}
      <Modal opened={dialog?.kind === 'rename'} onClose={close} centered size="sm" title="Rename household">
        {dialog?.kind === 'rename' && (
          <Stack gap="md">
            <TextInput label="Household name" required value={familyName} onChange={(e) => setFamilyName(e.currentTarget.value)} />
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>Cancel</Button>
              <Button loading={act.isPending} disabled={!familyName.trim()} onClick={() => rename(dialog.family)}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
